import { LitElement, css, html } from 'lit';
import { when } from 'lit/directives/when.js';
import {
  SpeechRecognitionController,
  type VoiceControllerErrorDetail,
  type VoiceControllerState,
  type VoiceControllerTranscriptDetail,
} from '../controllers/speech-recognition-controller';
import {
  resolveVoiceCaptureMessages,
  type VoiceCaptureMessageOverrides,
  type VoiceCaptureMessages,
  type VoiceCaptureUiLocale,
} from '../i18n/voice-capture-messages';

export type VoiceCaptureState =
  | VoiceControllerState
  | 'processing'
  | 'done';

export type VoiceCaptureErrorType =
  | VoiceControllerErrorDetail['type']
  | 'request';

export interface VoiceCaptureRequestPayload {
  text: string;
  context: string;
}

export interface VoiceCaptureRequestResult {
  [key: string]: unknown;
}

export interface VoiceCaptureErrorDetail {
  type: VoiceCaptureErrorType;
  originalError: unknown;
  code?: string;
  message: string;
}

export interface VoiceCaptureProcessedDetail {
  result: unknown;
  text: string;
  context: string;
}

export type { VoiceCaptureMessageOverrides, VoiceCaptureMessages, VoiceCaptureUiLocale };

export class VoiceCaptureElement extends LitElement {
  static properties = {
    context: { attribute: false },
    lang: { attribute: false },
    endpoint: { attribute: false },
    request: { attribute: false },
    uiLang: { attribute: false },
    messages: { attribute: false },
    status: { state: true },
    transcriptText: { state: true },
    errorMessage: { state: true },
    permissionMessage: { state: true },
  };

  static styles = css`
    :host {
      --vc-font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      --vc-font-size: 0.95rem;
      --vc-line-height: 1.5;
      --vc-gap: 0.875rem;
      --vc-padding: 1rem;
      --vc-border-radius: 0.875rem;
      --vc-border-color: #d7dce5;
      --vc-background: #ffffff;
      --vc-text-color: #162033;
      --vc-muted-color: #5b667a;
      --vc-accent-color: #2563eb;
      --vc-accent-contrast-color: #ffffff;
      --vc-danger-color: #9f1239;
      --vc-danger-background: #fff1f2;
      --vc-warning-color: #92400e;
      --vc-warning-background: #fff7ed;
      --vc-surface-alt: #f8fafc;
      --vc-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
      --vc-textarea-min-height: 10rem;
      --vc-button-radius: 999px;
      --vc-button-padding: 0.72rem 1rem;
      --vc-disabled-opacity: 0.6;
      display: block;
      font-family: var(--vc-font-family);
      color: var(--vc-text-color);
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    .container {
      display: grid;
      gap: var(--vc-gap);
      padding: var(--vc-padding);
      border: 1px solid var(--vc-border-color);
      border-radius: var(--vc-border-radius);
      background: var(--vc-background);
      box-shadow: var(--vc-shadow);
    }

    .controls {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      align-items: center;
    }

    .button-shell {
      display: inline-flex;
    }

    button {
      appearance: none;
      border: none;
      border-radius: var(--vc-button-radius);
      padding: var(--vc-button-padding);
      font: inherit;
      font-weight: 600;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      transition: transform 120ms ease, opacity 120ms ease, box-shadow 120ms ease;
    }

    button:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    button:disabled {
      opacity: var(--vc-disabled-opacity);
      cursor: not-allowed;
    }

    .record-button,
    .stop-button,
    .process-button,
    .retry-button {
      color: var(--vc-accent-contrast-color);
      background: var(--vc-accent-color);
      box-shadow: 0 8px 18px rgba(37, 99, 235, 0.24);
    }

    .secondary-button {
      background: var(--vc-surface-alt);
      color: var(--vc-text-color);
      border: 1px solid var(--vc-border-color);
      box-shadow: none;
    }

    textarea {
      width: 100%;
      min-height: var(--vc-textarea-min-height);
      resize: vertical;
      border: 1px solid var(--vc-border-color);
      border-radius: calc(var(--vc-border-radius) - 0.25rem);
      padding: 0.85rem 0.95rem;
      font: inherit;
      line-height: var(--vc-line-height);
      color: var(--vc-text-color);
      background: #ffffff;
    }

    textarea:focus {
      outline: 2px solid color-mix(in srgb, var(--vc-accent-color) 30%, transparent);
      border-color: var(--vc-accent-color);
    }

    textarea:disabled {
      background: var(--vc-surface-alt);
      color: var(--vc-muted-color);
    }

    .status-text {
      font-size: 0.875rem;
      color: var(--vc-muted-color);
      margin: 0;
    }

    .error-message,
    .permission-message {
      margin: 0;
      padding: 0.85rem 1rem;
      border-radius: calc(var(--vc-border-radius) - 0.25rem);
      border: 1px solid transparent;
      font-size: 0.92rem;
    }

    .error-message {
      color: var(--vc-danger-color);
      background: var(--vc-danger-background);
      border-color: color-mix(in srgb, var(--vc-danger-color) 16%, white);
    }

    .permission-message {
      color: var(--vc-warning-color);
      background: var(--vc-warning-background);
      border-color: color-mix(in srgb, var(--vc-warning-color) 18%, white);
    }

    .success-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      color: #166534;
      background: #ecfdf5;
      padding: 0.45rem 0.75rem;
      border-radius: 999px;
      font-size: 0.85rem;
      font-weight: 600;
    }
  `;

  context = '';
  override lang = 'es-MX';
  endpoint = '/foo';
  request?: ((payload: VoiceCaptureRequestPayload) => Promise<unknown>) | undefined;
  uiLang: VoiceCaptureUiLocale | string = 'en';
  messages: VoiceCaptureMessageOverrides = {};

  private controller: SpeechRecognitionController;
  private status: VoiceCaptureState = 'idle';
  private transcriptText = '';
  private errorMessage = '';
  private permissionMessage = '';
  private lastErrorDetail: VoiceCaptureErrorDetail | null = null;

  constructor() {
    super();
    this.controller = this.createController();
    this.handleControllerStateChange = this.handleControllerStateChange.bind(this);
    this.handleControllerTranscript = this.handleControllerTranscript.bind(this);
    this.handleControllerError = this.handleControllerError.bind(this);
    this.syncLocalizedMessages();
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.attachControllerListeners();
  }

  override disconnectedCallback(): void {
    this.detachControllerListeners();
    this.controller.abort();
    super.disconnectedCallback();
  }

  override updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('lang')) {
      this.controller.setLanguage(this.lang);
    }
  }

  override willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    if (changedProperties.has('uiLang') || changedProperties.has('messages')) {
      this.syncLocalizedMessages();
    }
  }

  override render() {
    const activeMessages = this.getActiveMessages();
    const isRecording = this.status === 'recording';
    const canProcess =
      this.status !== 'processing' &&
      this.status !== 'permission-denied' &&
      Boolean(this.context.trim()) &&
      Boolean(this.transcriptText.trim());
    const textareaDisabled = this.status === 'processing';

    return html`
      <section class="container" part="container">
        ${when(
          this.status === 'permission-denied',
          () => html`
            <p class="permission-message" part="permission-message">
              ${this.permissionMessage}
            </p>
          `,
        )}

        ${when(
          Boolean(this.errorMessage) && this.status !== 'permission-denied',
          () => html`
            <p class="error-message" part="error-message">${this.errorMessage}</p>
          `,
        )}

        ${when(
          this.status === 'done',
          () => html`<span class="success-badge">${activeMessages.successBadge}</span>`,
        )}

        <div class="controls">
          ${isRecording ? this.renderStopControl() : this.renderRecordControl()}
          ${this.renderProcessControl(canProcess)}
          ${this.renderSecondaryActions()}
        </div>

        <label>
          <span class="status-text">${activeMessages.transcriptLabel}</span>
          <textarea
            part="textarea"
            .value=${this.transcriptText}
            ?disabled=${textareaDisabled}
            @input=${this.handleTranscriptInput}
            placeholder=${activeMessages.transcriptPlaceholder}
          ></textarea>
        </label>

        <p class="status-text">${this.getStatusMessage()}</p>
      </section>
    `;
  }

  private renderRecordControl() {
    const activeMessages = this.getActiveMessages();
    const isBusy = this.status === 'processing';

    return html`
      <div class="button-shell" @click=${this.handleRecordClick}>
        <slot name="record-button">
          <button
            type="button"
            class="record-button"
            part="record-button"
            ?disabled=${isBusy}
          >
            <slot name="record-icon">🎙️</slot>
            <span>${activeMessages.startRecording}</span>
          </button>
        </slot>
      </div>
    `;
  }

  private renderStopControl() {
    const activeMessages = this.getActiveMessages();

    return html`
      <div class="button-shell" @click=${this.handleStopClick}>
        <slot name="stop-button">
          <button type="button" class="stop-button" part="stop-button">
            <slot name="stop-icon">⏹️</slot>
            <span>${activeMessages.stopRecording}</span>
          </button>
        </slot>
      </div>
    `;
  }

  private renderProcessControl(canProcess: boolean) {
    const activeMessages = this.getActiveMessages();

    return html`
      <div class="button-shell" @click=${this.handleProcessClick}>
        <slot name="process-button">
          <button
            type="button"
            class="process-button"
            part="process-button"
            ?disabled=${!canProcess}
          >
            <span>${activeMessages.processTranscript}</span>
          </button>
        </slot>
      </div>
    `;
  }

  private renderSecondaryActions() {
    const activeMessages = this.getActiveMessages();
    const showRetryRecording = this.status === 'permission-denied';
    const showRetryError = this.status === 'error';
    const showRerecord = ['captured', 'done', 'error'].includes(this.status);

    return html`
      ${when(
        showRetryRecording,
        () => html`
          <button
            type="button"
            class="secondary-button retry-button"
            @click=${this.handleRecordClick}
          >
            ${activeMessages.retryMicrophoneAccess}
          </button>
        `,
      )}
      ${when(
        showRetryError,
        () => html`
          <button type="button" class="secondary-button" @click=${this.handleRetryErrorClick}>
            ${activeMessages.retryAction}
          </button>
        `,
      )}
      ${when(
        showRerecord,
        () => html`
          <button type="button" class="secondary-button" @click=${this.handleRecordClick}>
            ${activeMessages.recordAgain}
          </button>
        `,
      )}
    `;
  }

  protected createController(): SpeechRecognitionController {
    return new SpeechRecognitionController({ lang: this.lang });
  }

  private attachControllerListeners(): void {
    this.controller.addEventListener('statechange', this.handleControllerStateChange as EventListener);
    this.controller.addEventListener('transcript', this.handleControllerTranscript as EventListener);
    this.controller.addEventListener('error', this.handleControllerError as EventListener);
  }

  private detachControllerListeners(): void {
    this.controller.removeEventListener('statechange', this.handleControllerStateChange as EventListener);
    this.controller.removeEventListener('transcript', this.handleControllerTranscript as EventListener);
    this.controller.removeEventListener('error', this.handleControllerError as EventListener);
  }

  private handleControllerStateChange(event: Event): void {
    const customEvent = event as CustomEvent<{ state: VoiceControllerState }>;

    if (this.status === 'processing') {
      return;
    }

    this.status = customEvent.detail.state;

    if (this.status !== 'error') {
      this.errorMessage = '';
    }

    if (this.status !== 'permission-denied') {
      this.permissionMessage = this.getActiveMessages().permissionDeniedMessage;
    }
  }

  private handleControllerTranscript(event: Event): void {
    const customEvent = event as CustomEvent<VoiceControllerTranscriptDetail>;
    this.transcriptText = customEvent.detail.transcript;
    this.dispatchComponentEvent('transcript', customEvent.detail);
  }

  private handleControllerError(event: Event): void {
    const customEvent = event as CustomEvent<VoiceControllerErrorDetail>;
    const detail: VoiceCaptureErrorDetail = {
      type: customEvent.detail.type,
      originalError: customEvent.detail.originalError,
      code: customEvent.detail.code,
      message: customEvent.detail.message,
    };
    const localizedDetail = this.localizeErrorDetail(detail);

    this.lastErrorDetail = detail;

    if (detail.type === 'permission-denied') {
      this.permissionMessage = localizedDetail.message;
      this.status = 'permission-denied';
      this.errorMessage = '';
    } else if (detail.type === 'unsupported' || detail.type === 'no-microphone') {
      this.status = 'error';
      this.errorMessage = localizedDetail.message;
    } else if (this.status !== 'recording') {
      this.status = 'error';
      this.errorMessage = localizedDetail.message;
    }

    this.dispatchComponentEvent('error', localizedDetail);
  }

  private handleTranscriptInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.transcriptText = target.value;
    this.dispatchComponentEvent('transcript', {
      finalTranscript: this.transcriptText,
      interimTranscript: '',
      transcript: this.transcriptText,
    });
  }

  private async handleRecordClick(): Promise<void> {
    if (this.status === 'processing') {
      return;
    }

    this.errorMessage = '';
    this.lastErrorDetail = null;
    this.status = 'idle';
    this.transcriptText = '';
    await this.controller.start();
  }

  private handleStopClick(): void {
    this.controller.stop();
  }

  private async handleProcessClick(): Promise<void> {
    if (
      this.status === 'processing' ||
      this.status === 'permission-denied' ||
      !this.transcriptText.trim()
    ) {
      return;
    }

    if (!this.context.trim()) {
      this.handleRequestFailure(new Error('The context property is required before processing text.'), 'missing-context');
      return;
    }

    this.errorMessage = '';
    this.lastErrorDetail = null;
    this.status = 'processing';

    try {
      const payload: VoiceCaptureRequestPayload = {
        text: this.transcriptText.trim(),
        context: this.context,
      };
      const result = this.request
        ? await this.request(payload)
        : await this.processWithFetch(payload);

      this.status = 'done';
      this.dispatchComponentEvent<VoiceCaptureProcessedDetail>('processed', {
        result,
        text: payload.text,
        context: payload.context,
      });
    } catch (error) {
      this.handleRequestFailure(error);
    }
  }

  private handleRetryErrorClick(): void {
    this.errorMessage = '';
    this.lastErrorDetail = null;
    this.status = this.transcriptText.trim() ? 'captured' : 'idle';
    this.permissionMessage = this.getActiveMessages().permissionDeniedMessage;
  }

  private async processWithFetch(payload: VoiceCaptureRequestPayload): Promise<unknown> {
    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}.`);
    }

    const contentType = response.headers.get('content-type') ?? '';

    if (contentType.includes('application/json')) {
      return response.json();
    }

    return response.text();
  }

  private handleRequestFailure(originalError: unknown, code = 'request-failed'): void {
    const detail: VoiceCaptureErrorDetail = {
      type: 'request',
      originalError,
      code,
      message:
        originalError instanceof Error
          ? originalError.message
          : this.getActiveMessages().requestErrorMessage,
    };
    const localizedDetail = this.localizeErrorDetail(detail);

    this.lastErrorDetail = detail;
    this.status = 'error';
    this.errorMessage = localizedDetail.message;
    this.dispatchComponentEvent('error', localizedDetail);
  }

  private getStatusMessage(): string {
    const activeMessages = this.getActiveMessages();

    switch (this.status) {
      case 'recording':
        return activeMessages.recordingStatus;
      case 'captured':
        return activeMessages.capturedStatus;
      case 'processing':
        return activeMessages.processingStatus;
      case 'done':
        return activeMessages.doneStatus;
      case 'permission-denied':
        return activeMessages.permissionDeniedStatus;
      case 'error':
        return this.lastErrorDetail?.type === 'request'
          ? activeMessages.requestErrorStatus
          : activeMessages.recognitionErrorStatus;
      default:
        return activeMessages.idleStatus;
    }
  }

  private getActiveMessages(): VoiceCaptureMessages {
    return resolveVoiceCaptureMessages(this.uiLang, this.messages);
  }

  private syncLocalizedMessages(): void {
    const activeMessages = this.getActiveMessages();

    if (this.status === 'permission-denied') {
      this.permissionMessage = this.getLocalizedErrorMessage(this.lastErrorDetail) ?? activeMessages.permissionDeniedMessage;
    } else {
      this.permissionMessage = activeMessages.permissionDeniedMessage;
    }

    if (this.status === 'error') {
      this.errorMessage = this.getLocalizedErrorMessage(this.lastErrorDetail);
    } else if (this.status !== 'permission-denied') {
      this.errorMessage = '';
    }
  }

  private localizeErrorDetail(detail: VoiceCaptureErrorDetail): VoiceCaptureErrorDetail {
    return {
      ...detail,
      message: this.getLocalizedErrorMessage(detail),
    };
  }

  private getLocalizedErrorMessage(detail: VoiceCaptureErrorDetail | null): string {
    const activeMessages = this.getActiveMessages();

    if (!detail) {
      return '';
    }

    if (detail.type === 'permission-denied') {
      return activeMessages.permissionDeniedMessage;
    }

    if (detail.type === 'unsupported') {
      return activeMessages.unsupportedMessage;
    }

    if (detail.type === 'no-microphone') {
      return activeMessages.noMicrophoneMessage;
    }

    if (detail.type === 'request') {
      if (detail.code === 'missing-context') {
        return activeMessages.missingContextMessage;
      }

      return detail.message || activeMessages.requestErrorMessage;
    }

    switch (detail.code) {
      case 'no-speech':
        return activeMessages.noSpeechMessage;
      case 'network':
        return activeMessages.networkMessage;
      case 'aborted':
        return activeMessages.abortedMessage;
      default:
        return activeMessages.recognitionMessage;
    }
  }

  private dispatchComponentEvent<TDetail>(eventName: string, detail: TDetail): void {
    this.dispatchEvent(
      new CustomEvent<TDetail>(eventName, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }
}

